import type {
  Symbol,
  OrderSide,
  OrderType,
  OrderState,
  Currency,
  TimeInForce,
  InstrumentType,
  ExecutionAlgorithm,
  Level1SnapshotJSON,
  CallPut,
  SecurityTypes,
  SpreadSymbol,
  WireProductSymbol,
} from './common';
import {type AlgoParams} from './algo';
import {type ExecutionTag, type TradingAccount} from './account';
import {type User} from './webgateway';
import {
  type TraderDBAccount, type ExecutionBroker, type Trader, type TradeIntention,
} from './traderdb';

// eslint-disable-next-line @typescript-eslint/no-restricted-types -- API schemas explicitly distinguish JSON null from omitted fields.
type NullValue = null;

/** Product wire shape used by partial Order Manager subscription updates. */
type OrderManagerWireSymbol = Omit<WireProductSymbol, 'segmentMic' | 'symbol'> & {
  segmentMic?: string | NullValue;
  symbol?: string | NullValue;
};

export type InternalCancelOrderRequest = {
  cancelRequest: CancelOrderRequest;
  internalOrder: OrderRecord;
  broker: ExecutionBroker;
};
export type CancelOrderRequest = {
  orderId: string;
  symbol: Symbol;
  orderSide: OrderSide;
  quantity: number;
  trader_id?: string;
  clearingString?: string;
  senderSubId?: string;
  clientOrderId?: string;
  orderPrice?: number;
};

export type CancelManyBySymbolRequest = {
  side?: OrderSide;
  symbol: {
    symbol: string;
    segmentMic: string;
  };
};

export enum OrderTypes {
  Algo = 'Algo',
  Manual = 'Manual',
  Spread = 'Synthetic Trader',
}
export enum Direction {
  Sell = 'sell',
  Buy = 'buy',
}
export type SelectedAccount = {
  account: TraderDBAccount;
  tradeIntention: TradeIntention;
  memo: string | undefined;
  customText1?: string | undefined;
  customText2: string | undefined;
  customText3: string | undefined;
};

export type OrderCloneParams = {
  ticketType: OrderTypes;
  symbols: Symbol[];
  algoName?: string;
  orderParameters: OrderParams;
  isDefaultParams?: boolean;
  orderLegs: OrderRecord[];
  accountExecCode: string;
  selectedAccount?: SelectedAccount;
};

export type OrderEntryOrder = {
  ticketType: OrderTypes;
  legs: OrderRecord[];
  params: OrderParams;
  isModifyingAlgo?: boolean;
  symbols?: Array<Symbol | SpreadSymbol>;
};

export type OrderAction = {
  request: OrderRequestInternal | InternalOrderModifyRequest | CancelOrderRequest;
  user: User;
  account: TraderDBAccount;
  clearingAccount: string;
  broker: ExecutionBroker;
  orderType: 'Futures Spread' | 'Futures' | 'Options';
};

export type OrderRequest = {
  account?: ExecutionTag;
  accountId?: string | number;
  clearingAccount?: string;
  clearingString: string;
  correlation_id?: string;
  customTag1?: string;
  customTag2?: string;
  customTag3?: string;
  execCode?: string;
  execution_gateway?: string;
  expirationDate?: Date;
  limit_price?: string | number;
  memo?: string;
  quantity: string | number;
  side: OrderSide;
  stop_limit?: string | number;
  symbol: Symbol;
  time_in_force: TimeInForce;
  trader_id?: string;
  type: OrderType;
  displayQuantity?: number;
};

export type OrderRequestInternal = {
  clientOrderId: string;
} & OrderRequest;
export type InternalOrderModifyRequest = {
  userUpdates: OrderModifyRequest;
  internalOrder: OrderRecord;
  symbol: Symbol;
};

export type OrderModifyRequest = {

  orderId: string;
  quantity?: number;
  displayQuantity?: number;
  price?: number | '';
};

export type OrderParams = Record<string, unknown> | ManualOrderParams | AlgoParams;

export type ManualOrderParams = {
  orderType: OrderType;
  timeInForce: TimeInForce;
  expirationDate?: string;
  limitPrice?: string;
  stopLimit?: string;
  displayQuantity?: number;
};

export type JpmAlgoParams = {
  endTime?: Date | string;
  maxParticipationPct?: number;
  startTime?: Date | string;
  urgency?: JpmAlgoUrgency;
  wouldPrice?: number;
};

export type OrderRecord = {
  account?: string | TradingAccount | TraderDBAccount | Trader | any; // @TODO: This needs to be 'string' eventually.
  neovestAccount?: string;
  algoId?: ExecutionAlgorithm;
  algoRunId?: string;
  algoType?: string | NullValue;
  averagePrice?: string | number;
  broker?: string;
  businessUnit?: string | NullValue;
  callPut?: CallPut | string | number | NullValue;
  cfiCode?: string | NullValue;
  clientOrderId?: string;
  comment?: string;
  commission?: unknown;
  commissionType?: unknown;
  contractYYYYMM?: string;
  createdAt?: string;
  cumulativeQuantity?: number;
  currency?: Currency;
  currencyString?: string;
  customText1?: string | NullValue;
  customText2?: string | NullValue;
  customText3?: string | NullValue;
  decimalPlaces?: number;
  description?: string;
  direction?: Direction;
  displayQuantity?: string | number | NullValue;
  dmaSession?: string;
  effectiveTimeUTC?: Date;
  euid?: string;
  exchangeSymbol?: string;
  exDestination?: string;
  exec_broker?: string;
  execId?: string;
  execType?: string;
  executionTag?: string | NullValue;
  executionType?: number | string;
  expirationDate?: string | Date | NullValue;
  expireTime?: Date;
  fillPrice?: number | undefined;
  fillQuantity?: number | undefined;
  firstNoticeDate?: Date;
  fixMessageId?: string;
  fixSymbol?: string;
  futureContractMonth?: string;
  initialOrderTime?: Date;
  instrumentType?: InstrumentType;
  internalAveragePrice?: number;
  internalFilledAveragePrice?: number;
  internalFilledQty?: number;
  internalFilledTotalQuantity?: number;
  isChildOrder?: boolean | undefined;
  jpmAlgoParams?: JpmAlgoParams;
  key?: string;
  lastBrokerCapacity?: unknown;
  lastMarket?: string;
  lastPrice?: number | undefined;
  lastQuantity?: number | undefined;
  lastUpdateTime?: Date;
  leavesQuantity?: number;
  leg_num?: number;
  leg_ratio?: number | string;
  leg_segment_mic?: string;
  leg_side?: string;
  legalEntity?: string;
  lei?: string;
  maturity?: Date;
  maturityDay?: unknown;
  maturityMonthYear?: string;
  maxPercentVol?: number;
  memo?: string;
  msgType?: string;
  multiLegReportingType?: number | string;
  num_legs?: number;
  optionContractMonth?: string;
  orderId?: string;
  orderLegs?: OrderRecord[];
  orderPrice?: string | number | NullValue;
  orderQuantity?: string | number;
  orderRecordKey?: string;
  orderSide?: OrderSide | string | number;
  orderSideString?: string;
  orderSource?: string;
  orderStatus?: string;
  orderType?: OrderType | string | number | NullValue;
  orderTypeString?: string;
  origClientOrderId?: string;
  parentId?: string;
  parentOrderId?: string;
  possDupe?: string;
  prices?: Level1SnapshotJSON;
  putCall?: boolean;
  putOrCall?: number;
  qep_symbol?: string;
  qepSymbol?: string;
  quantity?: number | string;
  remainingQty?: number;
  root?: string | NullValue;
  secondaryOrderID?: number;
  securityExchange?: string;
  securityId?: string;
  securityIdSource?: string;
  securityType?: SecurityTypes;
  segment_mic?: string;
  segmentMic?: string;
  senderLocationId?: string;
  senderSubId?: string;
  sendingTimeUtc?: Date;
  sendingTimeUTC?: Date | string;
  smpId?: string;
  status?: OrderState | string | number;
  statusString?: string;
  stopPrice?: number;
  strikePrice?: string | number | NullValue;
  sub_account?: string;
  symbol?: Symbol;
  targetOrderPrice?: number;
  text?: string;
  theoPrice?: number;
  timeInForce?: TimeInForce | string | number | NullValue;
  totalFilled?: string | number;
  trader?: string;
  traderId?: string;
  trader_id?: string;
  traderInfo?: string | Trader;
  transactionTime?: Date | string;
  transactTimeUtc?: Date;
  underlying?: string;
  underlyingRootSymbol?: string;
  urgency?: string;
  value?: string | number;
  venueAveragePrice?: number;
  venueFilledAveragePrice?: number;
  venueFilledQty?: number;
  venueFilledTotalQuantity?: number;
  wouldPrice?: number;
};

export type OrderManagerSubscriptionUpdate = {
  account?: string | NullValue;
  algoRunId?: string | NullValue;
  averagePrice?: string | number | NullValue;
  blotter?: unknown[] | NullValue;
  businessUnit?: string | NullValue;
  callPut?: string | number | NullValue;
  cfiCode?: string | NullValue;
  clientOrderId?: string | NullValue;
  createdAt?: string | NullValue;
  customText1?: string | NullValue;
  customText2?: string | NullValue;
  customText3?: string | NullValue;
  description?: string | NullValue;
  euid?: string | number | NullValue;
  executionTag?: string | NullValue;
  executionType?: string | number | NullValue;
  expireTime?: string | NullValue;
  expirationDate?: string | Date | NullValue;
  fillPrice?: string | number | NullValue;
  fillQuantity?: string | number | NullValue;
  fixSymbol?: string | NullValue;
  internalAveragePrice?: string | number | NullValue;
  internalFilledQty?: string | number | NullValue;
  lastPrice?: string | number | NullValue;
  lastQuantity?: string | number | NullValue;
  leavesQuantity?: string | number | NullValue;
  maturityMonthYear?: string | NullValue;
  memo?: string | NullValue;
  msgType?: string | number | NullValue;
  multiLegReportingType?: string | NullValue;
  orderId?: string | NullValue;
  orderPrice?: string | number | NullValue;
  orderQuantity?: string | number | NullValue;
  orderSide?: string | number | NullValue;
  orderSource?: string | NullValue;
  orderType?: string | number | NullValue;
  origClientOrderId?: string | NullValue;
  root?: string | NullValue;
  segmentMic?: string | NullValue;
  senderSubId?: string | NullValue;
  sendingTimeUTC?: string | Date | NullValue;
  status?: string | number | NullValue;
  stopPrice?: string | number | NullValue;
  strikePrice?: string | number | NullValue;
  symbol?: OrderManagerWireSymbol | NullValue;
  symbolObject?: Record<string, unknown> | NullValue;
  timeInForce?: string | number | NullValue;
  totalFilled?: string | number | NullValue;
  traderId?: string | NullValue;
  traderInfo?: TraderDBAccount | {email?: string | NullValue} | NullValue;
  transactionTime?: string | Date | NullValue;
  venueAveragePrice?: string | number | NullValue;
  venueFilledQty?: string | number | NullValue;
};

export type JpmAlgoUrgency = 'Low' | 'Medium' | 'High' | 'GetDone';

// Reference only do not use
// type FEOrderRecordPropsNotInDB = {
//   algoId?: ExecutionAlgorithm;
//   algoRunId?: string;
//   averagePrice?: number;
//   broker?: string;
//   businessUnit?: string;
//   callPut?: CallPut;
//   cfiCode?: string;
//   clientOrderId?: string;
//   comment?: string;
//   commission?: unknown;
//   commissionType?: unknown;
//   contractYYYYMM?: string;
//   createdAt?: string;
//   cumulativeQuantity?: number;
//   currency?: Currency;
//   currencyString?: string;
//   customText1?: string;
//   customText2?: string;
//   customText3?: string;
//   decimalPlaces?: number;
//   description?: string;
//   direction?: Direction;
//   dmaSession?: string;
//   effectiveTimeUTC?: Date;
//   euid?: string;
//   exchangeSymbol?: string;
//   exDestination?: string;
//   exec_broker?: string;
//   execId?: string;
//   execType?: string;
//   executionTag?: string;
//   executionType?: number | string;
//   expirationDate?: Date;
//   expireTime?: Date;
//   fillPrice?: number | undefined;
//   fillQuantity?: number | undefined;
//   firstNoticeDate?: Date;
//   fixMessageId?: string;
//   fixSymbol?: string;
//   futureContractMonth?: string;
//   initialOrderTime?: Date;
//   instrumentType?: InstrumentType;
//   internalAveragePrice?: number;
//   internalFilledAveragePrice?: number;
//   internalFilledQty?: number;
//   internalFilledTotalQuantity?: number;
//   isChildOrder?: boolean | undefined;
//   key?: string;
//   lastBrokerCapacity?: unknown;
//   lastMarket?: string;
//   lastPrice?: number | undefined;
//   lastQuantity?: number | undefined;
//   lastUpdateTime?: Date;
//   leavesQuantity?: number;
//   leg_num?: number;
//   leg_ratio?: number | string;
//   leg_segment_mic?: string;
//   leg_side?: string;
//   legalEntity?: string;
//   lei?: string;
//   maturity?: Date;
//   maturityDay?: unknown;
//   maturityMonthYear?: string;
//   memo?: string;
//   msgType?: string;
//   multiLegReportingType?: number | string;
//   num_legs?: number;
//   optionContractMonth?: string;
//   orderId?: string;
//   orderLegs?: OrderRecord[];
//   orderPrice?: number;
//   orderQuantity?: number;
//   orderRecordKey?: string;
//   orderSide?: OrderSide;
//   orderSideString?: string;
//   orderSource?: string;
//   orderStatus?: string;
//   orderType?: OrderType;
//   orderTypeString?: string;
//   origClientOrderId?: string;
//   parentId?: string;
//   parentOrderId?: string;
//   possDupe?: string;
//   prices?: Level1SnapshotJSON;
//   putCall?: boolean;
//   putOrCall?: number;
//   qep_symbol?: string;
//   qepSymbol?: string;
//   quantity?: number | string;
//   remainingQty?: number;
//   root?: string;
//   secondaryOrderID?: number;
//   securityExchange?: string;
//   securityId?: string;
//   securityIdSource?: string;
//   securityType?: SecurityTypes;
//   segment_mic?: string;
//   segmentMic?: string;
//   senderLocationId?: string;
//   senderSubId?: string;
//   sendingTimeUtc?: Date;
//   sendingTimeUTC?: Date | string;
//   smpId?: string;
//   status?: OrderState;
//   statusString?: string;
//   stopPrice?: number;
//   strikePrice?: number;
//   sub_account?: string;
//   symbol?: Symbol;
//   targetOrderPrice?: number;
//   text?: string;
//   theoPrice?: number;
//   timeInForce?: TimeInForce;
//   totalFilled?: number;
//   trader?: string;
//   traderId?: string;
//   trader_id?: string;
//   traderInfo?: Trader;
//   transactionTime?: Date;
//   transactTimeUtc?: Date;
//   underlying?: string;
//   underlyingRootSymbol?: string;
//   value?: string | number;
//   venueAveragePrice?: number;
//   venueFilledAveragePrice?: number;
//   venueFilledQty?: number;
//   venueFilledTotalQuantity?: number;
// };

export type OrderHistoryRecord = {
  trader_info: string;
  put_or_call?: string | undefined;
  root?: string | undefined;
  security_exchange?: string | undefined;
  fix_symbol?: string | undefined;
  strike_price?: number | undefined;
  symbol_info?: string | undefined;
  account?: string | undefined;
  algo_run_id?: string | undefined;
  average_price?: number | undefined;
  business_unit?: string | undefined;
  neovest_acct?: string | undefined;
  // Additional fields referenced in convertDBOrder
  cfi_code?: string | undefined;
  client_order_id?: string | undefined;
  created_time_utc?: string | undefined;
  custom_text1?: string | undefined;
  custom_text2?: string | undefined;
  custom_text3?: string | undefined;
  text?: string | undefined;
  exec_id?: string | undefined;
  execution_tag?: string | undefined;
  exec_type?: string | undefined;
  expireTime?: string | undefined;
  expiration_date?: string | undefined;
  last_price?: number | undefined;
  last_quantity?: number | undefined;
  internal_filled_average_price?: number | undefined;
  internal_filled_total_quantity?: number | undefined;
  leaves_quantity?: number | string | undefined;
  maturity_month_year?: string | undefined;
  memo?: string | undefined;
  msg_type?: string | undefined;
  multi_leg_reporting_type?: string | undefined;
  parent_order_id?: string | undefined;
  order_price?: number | undefined;
  order_quantity?: number | undefined;
  cumulative_quantity?: number | string | undefined;
  order_side?: string | undefined;
  order_source?: string | undefined;
  order_type?: string | undefined;
  orig_client_order_id?: string | undefined;
  sender_sub_id?: string | undefined;
  sending_time_utc?: string | undefined;
  order_status?: string | undefined;
  time_in_force?: string | undefined;
  transact_time_utc?: string | undefined;
  venue_filled_average_price?: number | undefined;
  venue_filled_total_quantity?: number | undefined;
  stop_price?: number | undefined;
  exec_broker?: string | undefined;
  displayQuantity?: number | undefined;
};

export type OrderKafkaPayload = {
  [key: string]: any;
  MsgType: string;
  SenderCompID: string;
  ParentID: string;
  MsgSeqNum: number;
  SenderSubID: string;
  Account: string;
  BusinessUnit: string;
  CFICode: string;
  ClOrdID: string;
  CustomText1: string;
  CustomText2: string;
  CustomText3: string;
  DeliverToCompID?: string;
  ExDestination?: string;
  ExecutionTag?: string;
  HandlInst?: string;
  LEI?: string;
  ManualOrderIndicator?: string;
  SendingTime?: string;
  OnBehalfOfCompID?: string;
  OrderID?: string;
  Currency?: string;
  Symbol?: string;
  SecurityExchange?: string;
  SecurityID?: string;
  SecurityIDSource?: string;
  SecurityType?: string;
  MaturityMonthYear?: string;
  OrdType?: string;
  OrderQty?: number;
  Price?: number;
  Side?: string;
  Text?: string;
  TimeInForce?: string;
  TransactTime?: string;
  ExecID?: string;
  ExecType?: string;
  OrdStatus?: string;
  AvgPx?: number;
  CumQty?: number;
  LeavesQty?: number;
  LastMkt?: string;
  LastPx?: number;
  LastQty?: number;
  NeovestAcct?: string;
  DMATraderName?: string;
  DMASession?: string;
  SymbolInfo?: KafkaSymbolInfo;
  TraderInfo?: KafkaTraderInfo;
  OrderSource?: string;
  Memo?: string;
  ExpireDate?: string;
  SecuritySubType?: string;
  PutOrCall?: string;
  RootSymbol?: string;
  StrikePrice?: number;
  StopPx?: number;
};

export type KafkaSymbolInfo = {
  trading_group?: string;
  qep_symbol?: string;
  exch_symbol?: string;
  expiry_code?: string;
  root?: string;
};

export type KafkaTraderInfo = {
  dsid?: string;
  email?: string;
  business_unit?: string;
  name?: string;
};
